import { useEffect, useState } from "react";
import {
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function EditSlider() {

    const { id } = useParams();

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [image, setImage] = useState(null);

    const [oldImage, setOldImage] = useState("");

    const [badge, setBadge] = useState("");

    const [title, setTitle] = useState("");

    const [description, setDescription] = useState("");

    const [order, setOrder] = useState("0");


    /*
    |--------------------------------------------------------------------------
    | Loading States
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Image URL
    |--------------------------------------------------------------------------
    */

    const getImageUrl = (imageName) => {

        if (!imageName) {
            return "";
        }

        const baseUrl = API_BASE_URL.replace("/api", "");

        return `${baseUrl}/storage/${imageName}`;
    };


    /*
    |--------------------------------------------------------------------------
    | Fetch Slider
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const fetchSlider = async () => {

            try {

                const response = await fetch(
                    `${API_BASE_URL}/admin/sliders/${id}`,
                    {
                        method: "GET",

                        headers: {
                            "Accept": "application/json"
                        },

                        credentials: "include"
                    }
                );


                const data = await response.json();


                if (response.ok && data.success) {

                    const slider = data.slider || {};


                    setOldImage(
                        slider.image || ""
                    );


                    setBadge(
                        slider.badge || ""
                    );


                    setTitle(
                        slider.title || ""
                    );


                    setDescription(
                        slider.description || ""
                    );


                    setOrder(
                        slider.order ?? 0
                    );

                } else {

                    Swal.fire({

                        icon: "error",

                        title: "Unable to Load Slider",

                        text:
                            data.message ||
                            "Slider not found."
                    });


                    navigate("/sliders");
                }

            } catch (error) {

                console.error(
                    "Fetch Slider Error:",
                    error
                );


                Swal.fire({

                    icon: "error",

                    title: "Server Error",

                    text:
                        "Unable to connect with server."
                });


                navigate("/sliders");

            } finally {

                setLoading(false);
            }
        };


        fetchSlider();

    }, [id, navigate]);


    /*
    |--------------------------------------------------------------------------
    | Handle Image Change
    |--------------------------------------------------------------------------
    */

    const handleImageChange = (e) => {

        const file = e.target.files[0];

        setImage(file || null);
    };


    /*
    |--------------------------------------------------------------------------
    | Update Slider
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        /*
        |----------------------------------------------------------------------
        | Frontend Validation
        |----------------------------------------------------------------------
        */

        if (!badge.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Badge Required",

                text: "Please enter slider badge."
            });

            return;
        }


        if (!title.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Title Required",

                text: "Please enter slider title."
            });

            return;
        }


        if (!description.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Description Required",

                text: "Please enter slider description."
            });

            return;
        }


        try {

            setSaving(true);


            const formData = new FormData();


            /*
            |----------------------------------------------------------------------
            | New Image
            |----------------------------------------------------------------------
            */

            if (image) {

                formData.append(
                    "image",
                    image
                );
            }


            /*
            |----------------------------------------------------------------------
            | Slider Content
            |----------------------------------------------------------------------
            */

            formData.append(
                "badge",
                badge.trim()
            );


            formData.append(
                "title",
                title.trim()
            );


            formData.append(
                "description",
                description.trim()
            );


            formData.append(
                "order",
                order
            );


            /*
            |----------------------------------------------------------------------
            | Laravel PUT Method
            |----------------------------------------------------------------------
            */

            formData.append(
                "_method",
                "PUT"
            );


            /*
            |----------------------------------------------------------------------
            | API Request
            |----------------------------------------------------------------------
            */

            const response = await fetch(
                `${API_BASE_URL}/admin/sliders/${id}`,
                {
                    method: "POST",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include",

                    body: formData
                }
            );


            const data = await response.json();


            /*
            |----------------------------------------------------------------------
            | Success
            |----------------------------------------------------------------------
            */

            if (response.ok && data.success) {

                await Swal.fire({

                    icon: "success",

                    title: "Updated!",

                    text:
                        data.message ||
                        "Slider updated successfully.",

                    timer: 1500,

                    showConfirmButton: false
                });


                navigate("/sliders");

            } else {

                /*
                |------------------------------------------------------------------
                | Laravel Validation Errors
                |------------------------------------------------------------------
                */

                let errorMessage =
                    data.message ||
                    "Something went wrong.";


                if (data.errors) {

                    const errors = Object.values(
                        data.errors
                    ).flat();


                    if (errors.length > 0) {

                        errorMessage =
                            errors.join("\n");
                    }
                }


                Swal.fire({

                    icon: "error",

                    title: "Unable to Update Slider",

                    text: errorMessage
                });
            }

        } catch (error) {

            console.error(
                "Update Slider Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server."
            });

        } finally {

            setSaving(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="container-fluid">

                <div className="text-center py-5">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    >
                    </div>


                    <p className="mt-2">
                        Loading slider...
                    </p>

                </div>

            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid py-4" style={{
                            maxHeight: "500px",
                            overflowX: "auto",
                            overflowY: "auto"
                        }}>

            {/* Header */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <h3 className="mb-0">
                    Edit Slider
                </h3>


                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/sliders")}
                >
                    Back
                </button>

            </div>


            {/* Form Card */}

            <div className="card shadow-sm">

                <div className="card-body">

                    <form onSubmit={handleSubmit}>


                        {/* Current Image */}

                        <div className="mb-4">

                            <label className="form-label fw-semibold">
                                Current Image
                            </label>


                            {oldImage ? (

                                <div className="mt-2">

                                    <img
                                        src={getImageUrl(oldImage)}
                                        alt="Current Slider"
                                        style={{
                                            width: "300px",
                                            height: "100px",
                                            objectFit: "cover",
                                            borderRadius: "5px",
                                            border: "1px solid #ddd"
                                        }}
                                    />

                                </div>

                            ) : (

                                <p className="text-muted">
                                    No current image
                                </p>

                            )}

                        </div>


                        {/* Change Image */}

                        <div className="mb-3">

                            <label className="form-label fw-semibold">
                                Change Image
                            </label>


                            <input
                                type="file"
                                className="form-control"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={handleImageChange}
                            />


                            <small className="text-muted">
                                Leave empty if you don't want to change the image.
                            </small>

                        </div>


                        {/* Badge */}

                        <div className="mb-3">

                            <label className="form-label fw-semibold">
                                Badge
                            </label>


                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. NEET PG 2026"
                                value={badge}
                                maxLength={100}
                                onChange={(e) =>
                                    setBadge(e.target.value)
                                }
                            />

                        </div>


                        {/* Title */}

                        <div className="mb-3">

                            <label className="form-label fw-semibold">
                                Title
                            </label>


                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter slider title"
                                value={title}
                                maxLength={255}
                                onChange={(e) =>
                                    setTitle(e.target.value)
                                }
                            />

                        </div>


                        {/* Description */}

                        <div className="mb-3">

                            <label className="form-label fw-semibold">
                                Description
                            </label>


                            <textarea
                                className="form-control"
                                rows="5"
                                placeholder="Enter slider description"
                                value={description}
                                onChange={(e) =>
                                    setDescription(e.target.value)
                                }
                            />

                        </div>


                        {/* Order */}

                        <div className="mb-4">

                            <label className="form-label fw-semibold">
                                Order
                            </label>


                            <input
                                type="number"
                                className="form-control"
                                min="0"
                                value={order}
                                onChange={(e) =>
                                    setOrder(e.target.value)
                                }
                            />

                        </div>


                        {/* Buttons */}

                        <div className="d-flex gap-2">

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >

                                {saving ? (

                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />

                                        Updating...
                                    </>

                                ) : (

                                    "Update Slider"

                                )}

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={saving}
                                onClick={() =>
                                    navigate("/sliders")
                                }
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
}


export default EditSlider;